import { downloadBlob, FileDownloadFormats } from 'acma-spa';
import {
  getApiClient,
  AssessorApplicationItem,
  IAssessorApplicationItem,
  ICallsignApplicationItem,
  Acma_amateurradioassessor_statuscode,
  Acma_amateurradiocallsign_statuscode,
} from '../api';
import { ClientApiProps } from '../common';

export interface ItemAction {
  label: string;
  action: string;
}

export interface MenuAction {
  actionType: string;
  label: string;
  actions: ItemAction[] | ItemAction;
}

interface IApplicationItem extends Omit<IAssessorApplicationItem, 'status'>, Omit<ICallsignApplicationItem, 'status'> {
  formName: string;
  type: string;
  status?: Acma_amateurradioassessor_statuscode | Acma_amateurradiocallsign_statuscode;
}

export interface Submission {
  referenceNumber: string;
  formName: string;
  lastModified: Date;
  actions: MenuAction[];
  dueDate?: Date;
  source: IApplicationItem;
}

export enum MenuItemOptions {
  Link = 'Link',
  DropdownLink = 'DropdownLink',
}

export enum ParticipantActions {
  Edit = 'Edit',
  View = 'View',
  Print = 'Download',
  PrintPdf = 'Pdf',
  PrintWord = 'Word',
}

const assessorFormName = 'Application to become an Accredited Assessor';
const callsignFormName = 'Callsign application';

export async function getDraftSubmissions(props: ClientApiProps) {
  const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
  try {
    const promise1 = client.assessorApplicationGetSaved();
    const promise2 = client.callsignApplicationGetSaved();
    const [result1, result2] = await Promise.all([promise1, promise2]);
    const result: IApplicationItem[] = [
      ...result1.map((r) => ({ ...r, formName: assessorFormName, type: 'assessor' })),
      ...result2.map((r) => ({ ...r, formName: callsignFormName, type: 'callsign' })),
    ];

    return result != null
      ? result.map(
          (s) =>
            ({
              referenceNumber: s.referenceNumber,
              formName: s.formName,
              lastModified: s.modifiedOn,
              actions: [
                {
                  actionType: MenuItemOptions.Link,
                  label: ParticipantActions.Edit,
                  actions: {
                    label: ParticipantActions.Edit,
                    action: ParticipantActions.Edit,
                  },
                },
                {
                  actionType: MenuItemOptions.DropdownLink,
                  label: ParticipantActions.Print,
                  actions: [
                    { label: 'PDF', action: ParticipantActions.PrintPdf },
                    { label: 'MS Word', action: ParticipantActions.PrintWord },
                  ],
                },
              ],
              source: s,
            }) as Submission,
        )
      : [];
  } catch (ex) {
    const error = ex instanceof Error ? ex : new Error(`Error downloading application`);
    console.error(error);
    throw error;
  }
}

export async function getPreviousSubmissions(search: string, props: ClientApiProps) {
  const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
  try {
    const promise1 = client.assessorApplicationGetHistory(search);
    const promise2 = client.callsignApplicationGetHistory(search);
    const [result1, result2] = await Promise.all([promise1, promise2]);
    const result: IApplicationItem[] = [
      ...result1.map((r) => ({ ...r, formName: assessorFormName, type: 'assessor' })),
      ...result2.map((r) => ({ ...r, formName: callsignFormName, type: 'callsign' })),
    ];

    return result != null
      ? result.map(
          (s) =>
            ({
              referenceNumber: s.referenceNumber,
              formName: s.formName,
              lastModified: s?.modifiedOn,
              actions: [
                {
                  actionType: MenuItemOptions.Link,
                  label: ParticipantActions.View,
                  actions: {
                    label: ParticipantActions.View,
                    action: ParticipantActions.View,
                  },
                },
                {
                  actionType: MenuItemOptions.DropdownLink,
                  label: ParticipantActions.Print,
                  actions: [
                    { label: 'PDF', action: ParticipantActions.PrintPdf },
                    { label: 'MS Word', action: ParticipantActions.PrintWord },
                  ],
                },
              ],
              source: s,
            }) as Submission,
        )
      : [];
  } catch (ex) {
    const error = ex instanceof Error ? ex : new Error(`Error downloading ACS2 declarations`);
    console.error(error);
    throw error;
  }
}

export function onSubmissionAction(submission: Submission, action: string, props: ClientApiProps) {
  switch (action) {
    case ParticipantActions.Edit:
    case ParticipantActions.View:
      window.location.hash = `#/forms/amateurradio/${submission.source.type}/${submission.source.id}`;
      break;
    case ParticipantActions.PrintPdf:
      downloadBlob(
        `${props.baseUrl}/api/${submission.source.type}Applications/print/${FileDownloadFormats.Pdf}/${submission.source.id}`,
        props.msalClient,
        [`https://${props.b2cDomainName}.onmicrosoft.com/AcmaApis/Call`],
        `${submission.referenceNumber ? submission.referenceNumber : 'registration'}.pdf`,
        'application/pdf',
      );
      break;
    case ParticipantActions.PrintWord:
      downloadBlob(
        `${props.baseUrl}/api/${submission.source.type}Applications/print/${FileDownloadFormats.Word}/${submission.source.id}`,
        props.msalClient,
        [`https://${props.b2cDomainName}.onmicrosoft.com/AcmaApis/Call`],
        `${submission.referenceNumber ? submission.referenceNumber : 'registration'}.docx`,
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      );
      break;
  }
}
